import AccountStore from "../stores/AccountStore"
import { IPayrollItemForAccountLevel, IHierarchyItem, IDateRange, EPayCalendarTypes, IDate, IPayrollPayCalendar, IPayGroup, IPaySettings } from "../models"
import moment, { Moment } from "moment"
import * as hierarchyHelper from "./hierarchyHelper"
import * as payGroupAndTypeHelper from "./payGroupAndTypeHelper"
import * as timeHelper from "./timeHelper"
import _ from "lodash"
import strings from "../localization/strings"
import * as timecardHelper from "./timecardHelper"
import * as utils from "./utils"

export function getSelectablePayrollHierarchyItems(): (IPayrollItemForAccountLevel | IHierarchyItem)[] {
    const payrollAccountItem: IPayrollItemForAccountLevel = {
        Id: null,
        Label: AccountStore.getCurrentAccount().Name,
        DefinitionLabel: "Account"
    }
    const payrollHierarchyDefinitionLevelId = AccountStore.getCurrentAccountSettings().PayrollHierarchyDefinitionLevelId
    let selectablePayrollHierarchyItems = []
    if (!payrollHierarchyDefinitionLevelId) {
        selectablePayrollHierarchyItems.push(payrollAccountItem)
    } else {
        selectablePayrollHierarchyItems = hierarchyHelper.getHierarchyItemsByDefinitionLevelId(payrollHierarchyDefinitionLevelId)
    }
    return selectablePayrollHierarchyItems
}

export function getAutoAssignPayrollHierarchyItem(defaultHierarchyItemId, selectablePayrollHierarchyItems) {
    const payrollHierarchyDefinitionLevelId = AccountStore.getCurrentAccountSettings().PayrollHierarchyDefinitionLevelId
    if (!payrollHierarchyDefinitionLevelId) {
        return selectablePayrollHierarchyItems[0]
    } else {
        const defaultHierarchyItem = AccountStore.getCurrentAccount().HierarchyItems.find((item) => item.Id === defaultHierarchyItemId)
        const defaultHierarchyDefinitionLevel = defaultHierarchyItem
            ? AccountStore.getCurrentAccount().HierarchyDefinitions.find((item) => item.Id === defaultHierarchyItem.HierarchyDefinitionId).Level
            : -1
        const payrollHierarchyDefinitionItem = AccountStore.getCurrentAccount().HierarchyDefinitions.find((item) => item.Id === payrollHierarchyDefinitionLevelId)
        if (defaultHierarchyDefinitionLevel >= payrollHierarchyDefinitionItem.Level) {
            const count = defaultHierarchyItem.HierarchyList.length
            const assignPayrollHierarchyItemId = defaultHierarchyItem.HierarchyList[count - payrollHierarchyDefinitionItem.Level - 1]
            return AccountStore.getCurrentAccount().HierarchyItems.find((item) => item.Id === assignPayrollHierarchyItemId)
        } else {
            const payrollLevelHierarchyItem = selectablePayrollHierarchyItems.find((item) => item.HierarchyList.find((listItem) => listItem === defaultHierarchyItemId))
            return payrollLevelHierarchyItem ? payrollLevelHierarchyItem : selectablePayrollHierarchyItems[0]
        }
    }
}

export function getPayrollDates(date: IDate, payGroupId: string): IDateRange {
    const payGroup = payGroupAndTypeHelper.getPayGroupById(payGroupId)
    const payrollPayCalendar = getPayrollPayCalendarByPayGroup(payGroup)
    return getPayrollDatesByPayCalendar(date, payrollPayCalendar)
}

export function getPayrollDatesByPayGroupPaySettings(date: IDate, payGroupPaySettings: IPaySettings): IDateRange {
    const payrollPayCalendar = getPayrollPayCalendarByPayGroupPaySettings(payGroupPaySettings)
    return getPayrollDatesByPayCalendar(date, payrollPayCalendar)
}

export function getPayrollDatesByPayCalendar(date: IDate, payrollPayCalendar: IPayrollPayCalendar): IDateRange {
    let startDateRange, endDateRange
    const referenceDate = moment(date).add(-payrollPayCalendar.PayPeriodDayStartTime24Hour, "hour")
    const startDay = payrollPayCalendar.PayPeriodStartDay

    switch (payrollPayCalendar.PayCalendarType) {
        case EPayCalendarTypes.Weekly:
            startDateRange = timeHelper.getStartOfWeek(startDay, _.cloneDeep(referenceDate))
            endDateRange = timeHelper.getEndOfWeek(startDay, referenceDate)
            break

        case EPayCalendarTypes.Biweekly:
            const startOfWeek = timeHelper.getStartOfWeek(startDay, referenceDate).startOf("day")
            const payCalendarStartDate = timeHelper.keepConvertUTCToLocal(payrollPayCalendar.PayCalendarStartDate)
            var totalWeeks = Math.round(startOfWeek.diff(payCalendarStartDate, "day") / 7)
            //If we are an even number of weeks since the start of pay calendar, then we are in the 1st week of bi-weekly cycle
            if (totalWeeks % 2 == 0) {
                startDateRange = _.cloneDeep(startOfWeek)
                endDateRange = timeHelper.getEndOfWeek(startDay, startOfWeek.add(1, "week"))
            }
            //If we are an odd number of weeks since the start of pay calendar, then we are in the 2nd week of bi-weekly cycle
            else {
                startDateRange = _.cloneDeep(startOfWeek).add(-1, "week")
                endDateRange = timeHelper.getEndOfWeek(startDay, startOfWeek)
            }
            break

        case EPayCalendarTypes.Monthly:
            const payDayOfMonth = payrollPayCalendar.PayCalendarMonthlyPayDay1
            const dateOfMonth = _.cloneDeep(referenceDate).startOf("month").date(payDayOfMonth)
            if (referenceDate.isSameOrAfter(dateOfMonth)) {
                startDateRange = _.cloneDeep(dateOfMonth)
                endDateRange = dateOfMonth.add(1, "month").add(-1, "day").endOf("day")
            }
            else {
                startDateRange = _.cloneDeep(dateOfMonth).add(-1, "month")
                endDateRange = dateOfMonth.add(-1, "day").endOf("day")
            }

            break
        case EPayCalendarTypes.SemiMonthly:
            var payDayOfMonth1 = payrollPayCalendar.PayCalendarMonthlyPayDay1
            var payDayOfMonth2 = payrollPayCalendar.PayCalendarMonthlyPayDay2

            var payDate1 = _.cloneDeep(referenceDate).startOf("month").date(payDayOfMonth1)
            var payDate2 = _.cloneDeep(referenceDate).startOf("month").date(payDayOfMonth2)


            //Reference date is within 1st paydate of the month
            if (referenceDate.isBefore(payDate1)) {
                endDateRange = payDate1.add(-1, "day").endOf("day")
                startDateRange = _.cloneDeep(endDateRange).add(-1, "month").date(payDayOfMonth2).startOf("day")
            }
            //Reference date is within 2nd paydate of the month
            else if (referenceDate.isBefore(payDate2)) {
                startDateRange = payDate1.startOf("day")
                endDateRange = payDate2.add(-1, "day").endOf("day")
            }
            else {
                startDateRange = payDate2.startOf("day")
                endDateRange = _.cloneDeep(startDateRange).add(1, "month").date(payDayOfMonth1).add(-1, "day").endOf("day")
            }

            break
    }

    return {
        StartDate: (startDateRange as Moment).add(payrollPayCalendar.PayPeriodDayStartTime24Hour, "hour").format(),
        EndDate: (endDateRange as Moment).add(payrollPayCalendar.PayPeriodDayStartTime24Hour, "hour").format()
    }
}

export function getThisLastPayrollPeriod(payGroupId: string) {
    const thisPayrollPeriod = getPayrollDates(moment(), payGroupId)
    const lastPayrollPeriod = getPayrollDates(moment(thisPayrollPeriod.StartDate).add(-1, "day"), payGroupId)
    return { thisPayrollPeriod, lastPayrollPeriod }
}

export function getThisLastPayrollPeriodByPayrollPayCalendar(payrollPayCalendar: IPayrollPayCalendar) {
    const thisPayrollPeriod = getPayrollDatesByPayCalendar(moment(), payrollPayCalendar)
    const lastPayrollPeriod = getPayrollDatesByPayCalendar(moment(thisPayrollPeriod.StartDate).add(-1, "day"), payrollPayCalendar)
    return { thisPayrollPeriod, lastPayrollPeriod }
}

export function getValidClosePayroll(payPeriodEndDate: IDate, payGroupPaySettings: IPaySettings): boolean {
    const thisPayrollPeriod = getPayrollDatesByPayGroupPaySettings(moment(), payGroupPaySettings)
    return moment(timeHelper.keepConvertUTCToLocal(payPeriodEndDate)).isBefore(thisPayrollPeriod.StartDate)
}

export function getPayGroupIdsByPayrollPayCalendar(payrollPayCalendar: IPayrollPayCalendar): string[] {
    return getPayGroupsByPayrollPayCalendar(payrollPayCalendar).map(i => i.Id)
}

export function getValidPayrollPayGroups() {
    const account = AccountStore.getCurrentAccount()
    if (account.AccountSettings.FeatureSettings.EnablePayGroupAssignEmployeeLevel) {
        return account.PayGroups.filter(i => !i.Deleted)
    }
    else {
        let validPayrollPayGroups: IPayGroup[] = []
        const payrollHierarchyItems = account.HierarchyItems.filter((item) => item.HierarchyDefinitionId === account.AccountSettings.PayrollHierarchyDefinitionLevelId)
        if (payrollHierarchyItems && payrollHierarchyItems.length > 0) {
            payrollHierarchyItems.forEach((hierarchyItem) => {
                const payGroup = payGroupAndTypeHelper.getPayGroupByPayrollPayGroupData({
                    PayrollHierarchyItemId: hierarchyItem.Id,
                    PayrollPayGroupId: null
                })
                !validPayrollPayGroups.find((item) => item.Id === payGroup.Id) && validPayrollPayGroups.push(payGroup)
            })
        } else {
            validPayrollPayGroups = account.PayGroups.filter((item) => item.Id === account.HierarchyItemSettings.PayGroupSettings.PayGroupId)
        }
        return validPayrollPayGroups
    }
}

export function getValidPayrollPayCalendars() {
    const validPayrollPayCalendars: IPayrollPayCalendar[] = []
    const validPayrollPayGroups = getValidPayrollPayGroups()
    validPayrollPayGroups.forEach(payGroup => {
        const payrollPayCalendar = getPayrollPayCalendarByPayGroup(payGroup)
        validPayrollPayCalendars.length === 0 ?
            validPayrollPayCalendars.push(payrollPayCalendar) :
            !validPayrollPayCalendars.find(i => samePayrollPayCalendars(i, payrollPayCalendar)) &&
            validPayrollPayCalendars.push(payrollPayCalendar)
    })
    return validPayrollPayCalendars
}

export function getPayGroupsByPayrollPayCalendar(payrollPayCalendar: IPayrollPayCalendar): IPayGroup[] {
    return getValidPayrollPayGroups().filter(i => samePayrollPayCalendars(payrollPayCalendar, getPayrollPayCalendarByPayGroup(i)))
}

export function getSelectedPayrollPayCalendar(selectedPayGroup: IPayGroup = null) {
    const validPayrollPayCalendars = getValidPayrollPayCalendars()
    const storedSelectedPayCalendarLabel = utils.getCookie("selected_payroll_pay_calendar_label")
    const storedSelectedPayCalendar = storedSelectedPayCalendarLabel && validPayrollPayCalendars.find(i => i.PayCalendarLabel === storedSelectedPayCalendarLabel)
    const selectedPayrollPayCalendar = storedSelectedPayCalendar ?
        storedSelectedPayCalendar :
        selectedPayGroup ?
            getPayrollPayCalendarByPayGroup(selectedPayGroup) :
            validPayrollPayCalendars[0]
    return selectedPayrollPayCalendar
}

export function getPayrollPayCalendarByPayGroup(payGroup: IPayGroup): IPayrollPayCalendar {
    return getPayrollPayCalendarByPayGroupPaySettings(payGroup.PaySettings)
}

export function getPayrollPayCalendarByPayGroupPaySettings(payGroupPaySettings: IPaySettings): IPayrollPayCalendar {
    const payrollPayCalendar: IPayrollPayCalendar = {
        PayPeriodStartDay: payGroupPaySettings.PayPeriodStartDay,
        PayCalendarType: payGroupPaySettings.PayCalendarType,
        PayPeriodDayStartTime24Hour: payGroupPaySettings.PayPeriodDayStartTime24Hour,
        PayCalendarStartDate: payGroupPaySettings.PayCalendarStartDate,
        PayPeriodNumberOfWeeks: payGroupPaySettings.PayPeriodNumberOfWeeks,
        PayCalendarMonthlyPayDay1: payGroupPaySettings.PayCalendarMonthlyPayDay1,
        PayCalendarMonthlyPayDay2: payGroupPaySettings.PayCalendarMonthlyPayDay2
    }
    payrollPayCalendar.PayCalendarLabel = getPayrollPayCalendarLabel(payrollPayCalendar)
    return payrollPayCalendar
}

export function samePayrollPayCalendars(payrollPayCalendar1: IPayrollPayCalendar, payrollPayCalendar2: IPayrollPayCalendar) {
    return payrollPayCalendar1.PayPeriodStartDay === payrollPayCalendar2.PayPeriodStartDay &&
        payrollPayCalendar1.PayCalendarType === payrollPayCalendar2.PayCalendarType &&
        moment(payrollPayCalendar1.PayCalendarStartDate).isSame(payrollPayCalendar2.PayCalendarStartDate) &&
        payrollPayCalendar1.PayPeriodNumberOfWeeks === payrollPayCalendar2.PayPeriodNumberOfWeeks &&
        payrollPayCalendar1.PayCalendarMonthlyPayDay1 === payrollPayCalendar2.PayCalendarMonthlyPayDay1 &&
        payrollPayCalendar1.PayCalendarMonthlyPayDay2 === payrollPayCalendar2.PayCalendarMonthlyPayDay2 &&
        payrollPayCalendar1.PayCalendarLabel === payrollPayCalendar2.PayCalendarLabel
}

export function getPayrollPayCalendarLabel(payrollPayCalendar: IPayrollPayCalendar) {
    let returnVal
    const payrollDates = getPayrollDatesByPayCalendar(moment(), payrollPayCalendar)
    const timecardDates = timecardHelper.getTimecardDateByPayrollPayCalendar(moment(), payrollPayCalendar)
    switch (payrollPayCalendar.PayCalendarType) {
        case EPayCalendarTypes.Weekly:
            returnVal = `${strings.Weekly} (${moment(payrollDates.StartDate).format("ddd")}-${moment(payrollDates.EndDate).format("ddd")})`
            break
        case EPayCalendarTypes.Biweekly:
            returnVal = `${strings.Bi_Weekly} (StartDay: ${moment(timecardDates.StartDate).format("ddd")},  StartDate: ${moment(payrollPayCalendar.PayCalendarStartDate).format("DD-MM-YYYY")})`
            break
        case EPayCalendarTypes.Monthly:
            returnVal = `${strings.Monthly} (StartDay: ${moment(timecardDates.StartDate).format("ddd")},  StartDate: ${payrollPayCalendar.PayCalendarMonthlyPayDay1})`
            break
        case EPayCalendarTypes.SemiMonthly:
            returnVal = `${strings.Semi_Monthly} (StartDay: ${moment(timecardDates.StartDate).format("ddd")},  StartDates: ${payrollPayCalendar.PayCalendarMonthlyPayDay1}, ${payrollPayCalendar.PayCalendarMonthlyPayDay2})`
            break
    }
    return returnVal
}
